import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  User,
  Globe,
  Mic,
  Upload,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

import narratorService from "@/services/narratorService";
import authService from "@/services/authService";
import NarratorHeader from "@/components/narrator/NarratorHeader";

// Debounce utility
function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Italian",
  "Hindi",
  "Chinese",
  "Japanese",
  "Arabic",
  "Korean",
];

const AVAILABILITY_OPTIONS = [
  { value: "AVAILABLE", label: "Available" },
  { value: "BUSY", label: "Busy" },
  { value: "ON_BREAK", label: "On Break" },
];

function OnboardingShell({ children, autosaveStatus, lastSaved }) {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#fffaf2]" data-testid="narrator-onboarding">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_80%_8%,rgba(245,207,117,0.24),transparent_25%),linear-gradient(180deg,#ffffff_0%,#fffaf2_42%,#fff1d8_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[58vh] opacity-60 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.18)_0_1px,transparent_1px_18px)]" />
      <NarratorHeader autosaveStatus={autosaveStatus} lastSaved={lastSaved} />
      {children}
    </div>
  );
}

function GoldStepper({ steps, currentStep }) {
  return (
    <div className="mb-6 grid grid-cols-4 items-start gap-3">
      {steps.map((step, index) => {
        const active = index <= currentStep || step.completed;
        return (
          <div key={step.label} className="relative flex flex-col items-start">
            <div className="flex w-full items-center">
              <div
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border text-sm font-bold ${
                  active
                    ? "border-[#c98905] bg-gradient-to-br from-[#f5cf75] to-[#b77900] text-white shadow-[0_8px_20px_rgba(185,119,0,0.22)]"
                    : "border-[#d8bd7a] bg-white text-slate-700"
                }`}
              >
                {step.completed ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
              </div>
              {index < steps.length - 1 ? (
                <div className="mx-3 h-0.5 flex-1 bg-[#eadfcd]">
                  <div className={`h-full ${active ? "w-full bg-[#c98905]" : "w-0"}`} />
                </div>
              ) : null}
            </div>
            <p className={`mt-2 text-xs font-semibold ${active ? "text-[#b77900]" : "text-slate-700"}`}>
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function GoldSectionCard({ icon: Icon, label, children }) {
  return (
    <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)]">
      <CardContent className="p-6">
        <div className="flex gap-5">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#f5cf75] to-[#b77900] text-white shadow-[0_12px_28px_rgba(185,119,0,0.20)]">
            <Icon className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              className="mb-4 text-xl font-semibold text-black"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {label}
            </h2>
            {children}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressItem({ done, label }) {
  return (
    <div className="flex items-center gap-3">
      <CheckCircle2 className={`h-5 w-5 ${done ? "text-[#c98905] fill-[#c98905]/15" : "text-[#d8bd7a]"}`} />
      <span className="text-sm font-medium text-black">{label}</span>
    </div>
  );
}

export default function NarratorOnboarding() {
  const navigate = useNavigate();

  // Profile state
  const [profile, setProfile] = useState(null);
  const [demos, setDemos] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [country, setCountry] = useState("");
  const [languages, setLanguages] = useState(["English"]);
  const [gender, setGender] = useState("ANY");
  const [availability, setAvailability] = useState("AVAILABLE");
  const [accentTags, setAccentTags] = useState("");

  // UI state
  const [loading, setLoading] = useState(true);
const [currentStep, setCurrentStep] = useState(0);
const [autosaveStatus, setAutosaveStatus] = useState("idle");
const [lastSaved, setLastSaved] = useState(null);
const [isUploadOpen, setIsUploadOpen] = useState(false);
const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      let profileData = await narratorService.getProfile().catch(() => null);

      // Auto-create profile if none exists
      if (!profileData) {
        await narratorService.createProfile();
        profileData = await narratorService.getProfile();
      }

      setProfile(profileData);

      // Populate form
      if (profileData) {
        setDisplayName(profileData.display_name || "");
        setBio(profileData.bio || "");
        setCountry(profileData.country || "");
        setLanguages(profileData.languages || ["English"]);
        setGender(profileData.gender || "ANY");
        setAvailability(profileData.availability_status || "AVAILABLE");
        setAccentTags(profileData.accent_tags || "");
      }

      // Load demos and status
      const [demosData, statusData] = await Promise.all([
        narratorService.getDemos().catch(() => []),
        narratorService.getOnboardingStatus().catch(() => null),
      ]);

      setDemos(Array.isArray(demosData) ? demosData : []);
      setOnboardingStatus(statusData);

      // Determine current step
      if (!profileData?.bio || !profileData?.country) {
        setCurrentStep(0);
      } else if (!demosData || demosData.length === 0) {
        setCurrentStep(2);
      } else {
        setCurrentStep(3);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 401) {
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Narrator role required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce(async (updateData) => {
      try {
        setAutosaveStatus("saving");
        await narratorService.updateProfile(updateData);
        setAutosaveStatus("saved");
        setLastSaved(Date.now());
      } catch (error) {
        console.error("Autosave error:", error);
        setAutosaveStatus("error");
      }
    }, 800),
    []
  );

  const handleFieldUpdate = (field, value) => {
    const updateData = { [field]: value };
    debouncedUpdate(updateData);
  };

  const toggleLanguage = (lang) => {
    const newLanguages = languages.includes(lang)
      ? languages.filter((l) => l !== lang)
      : [...languages, lang];
    
    if (newLanguages.length > 0) {
      setLanguages(newLanguages);
      handleFieldUpdate("languages", newLanguages);
    }
  };

 const handleUploadDemo = async () => {
  console.log("Upload button clicked");
  
  try {
    if (!selectedFile) {
      console.log("No file selected");
      toast.error("Please select a file first");
      return;
    }

    console.log("Selected file:", selectedFile);

    const formData = new FormData();
    formData.append("file", selectedFile);

    const response = await narratorService.uploadDemo(formData);

    console.log("Upload success:", response);
    toast.success("Demo uploaded successfully");

  } catch (error) {
    console.log("Upload error:", error);
    toast.error("Upload failed");
  }
};

  const handleSubmitDemo = async (demoId) => {
    try {
      await narratorService.submitDemo(demoId);
      toast.success("Demo submitted for review");
      await loadData();
    } catch (error) {
      console.error("Error submitting demo:", error);
      toast.error("Failed to submit demo");
    }
  };

  const steps = [
    { label: "Basics", completed: !!profile?.display_name && !!profile?.bio },
    { label: "Languages", completed: languages.length > 0 },
    { label: "Demo", completed: demos.length > 0 },
    {
      label: "Review",
      completed: onboardingStatus?.portfolio_status === "APPROVED",
    },
  ];

  if (loading) {
    return (
      <OnboardingShell>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b77900] mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading onboarding...</p>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell autosaveStatus={autosaveStatus} lastSaved={lastSaved}>
      <main className="relative z-10 mx-auto max-w-[1640px] px-5 py-8 md:px-8">

        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Narrator Onboarding
          </h1>
          <p className="text-slate-600">
            Complete your profile to start receiving book assignments
          </p>
        </div>

        {/* Progress */}
        <GoldStepper steps={steps} currentStep={currentStep} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Basics */}
            <GoldSectionCard
              icon={User}
              label="Step 1: Profile Basics"
            >
              <div className="space-y-4">
                <div>
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      handleFieldUpdate("display_name", e.target.value);
                    }}
                    placeholder="Your professional name"
                    className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                    data-testid="input-display-name"
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => {
                      setBio(e.target.value);
                      handleFieldUpdate("bio", e.target.value);
                    }}
                    placeholder="Tell publishers about your voice and experience..."
                    rows={4}
                    className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                    data-testid="input-bio"
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      handleFieldUpdate("country", e.target.value);
                    }}
                    placeholder="e.g., United States"
                    className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                    data-testid="input-country"
                  />
                </div>

                <div>
                  <Label>Gender Voice Type</Label>
                  <div className="flex gap-2 mt-2">
                    {["MALE", "FEMALE", "ANY"].map((g) => (
                      <Button
                        key={g}
                        size="sm"
                        onClick={() => {
                          setGender(g);
                          handleFieldUpdate("gender", g);
                        }}
                        variant={gender === g ? "default" : "outline"}
                        className={
                          gender === g
                            ? "bg-[#c98905] text-white hover:bg-[#a56f00]"
                            : "border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]"
                        }
                        data-testid={`gender-${g.toLowerCase()}`}
                      >
                        {g}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </GoldSectionCard>

            {/* Step 2: Languages */}
            <GoldSectionCard
              icon={Globe}
              label="Step 2: Languages & Availability"
            >
              <div className="space-y-4">
                <div>
                  <Label>Languages You Speak</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <Badge
                        key={lang}
                        onClick={() => toggleLanguage(lang)}
                        className={`cursor-pointer transition-all ${
                          languages.includes(lang)
                            ? "bg-[#c98905] text-white hover:bg-[#a56f00]"
                            : "border border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]"
                        }`}
                        data-testid={`lang-${lang.toLowerCase()}`}
                      >
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="accentTags">Accent Tags (comma separated)</Label>
                  <Input
                    id="accentTags"
                    value={accentTags}
                    onChange={(e) => {
                      setAccentTags(e.target.value);
                      handleFieldUpdate("accent_tags", e.target.value);
                    }}
                    placeholder="e.g., British, American, Australian"
                    className="border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                    data-testid="input-accents"
                  />
                </div>

                <div>
                  <Label>Availability Status</Label>
                  <div className="flex gap-2 mt-2">
                    {AVAILABILITY_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        size="sm"
                        onClick={() => {
                          setAvailability(opt.value);
                          handleFieldUpdate("availability_status", opt.value);
                        }}
                        variant={availability === opt.value ? "default" : "outline"}
                        className={
                          availability === opt.value
                            ? "bg-[#c98905] text-white hover:bg-[#a56f00]"
                            : "border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]"
                        }
                        data-testid={`avail-${opt.value.toLowerCase()}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </GoldSectionCard>

            {/* Step 3: Demo Upload */}
            <GoldSectionCard
              icon={Upload}
              label="Step 3: Upload Demo Clip"
            >
              <div className="space-y-4">
                <div className="p-8 border-2 border-dashed border-[#d8bd7a] rounded-xl text-center bg-[#fffaf2]">
                  <Mic className="w-12 h-12 mx-auto mb-4 text-[#c98905]" />
                  <p className="text-sm text-slate-600 mb-2">
                    Upload a demo audio clip (1-3 minutes)
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Supported formats: .mp3, .wav, .m4a
                  </p>



                  <Button
  onClick={() => setIsUploadOpen(true)}
  className="bg-[#c98905] text-white hover:bg-[#a56f00]"
  data-testid="upload-demo-btn"
>
  <Upload className="w-4 h-4 mr-2" />
  Upload Demo (Stub)
</Button>


                </div>

                {demos.length > 0 && (
                  <div className="space-y-2">
                    <Label>Your Demos</Label>
                    {demos.map((demo) => (
                      <div
                        key={demo.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      >
                        <div>
                          <p className="font-medium text-black">{demo.title}</p>
                          <Badge
                            className={
                              demo.status === "APPROVED"
                                ? "bg-green-100 text-green-800"
                                : demo.status === "SUBMITTED"
                                ? "bg-[#fff2d8] text-[#9a6500]"
                                : "bg-slate-100 text-slate-800"
                            }
                          >
                            {demo.status}
                          </Badge>
                        </div>
                        {demo.status === "DRAFT" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSubmitDemo(demo.id)}
                            data-testid={`submit-demo-${demo.id}`}
                          >
                            Submit for Review
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </GoldSectionCard>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)]">
              <CardHeader>
                <CardTitle
                  className="text-lg"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Onboarding Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ProgressItem done={steps[0].completed} label="Profile basics" />
                <ProgressItem done={steps[1].completed} label="Languages set" />
                <ProgressItem done={steps[2].completed} label="Demo uploaded" />
                <ProgressItem done={steps[3].completed} label="Portfolio approved" />
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/narrator")}
                className="flex-1 border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <Button
                onClick={() => navigate("/narrator/demos")}
                className="flex-1 bg-[#c98905] text-white hover:bg-[#a56f00]"
                data-testid="go-to-demos-btn"
              >
                Demos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </main>

{isUploadOpen && (
  <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
    <div className="bg-white rounded-xl w-[500px] p-6 shadow-xl relative">

      {/* Close button */}
      <button
        className="absolute top-4 right-4 text-slate-500 hover:text-black"
        onClick={() => setIsUploadOpen(false)}
      >
        ✕
      </button>

      <h2 className="text-xl font-semibold mb-2">Upload Demo</h2>
      <p className="text-sm text-slate-500 mb-6">
        Upload an audio demo clip to showcase your voice
      </p>

      <div className="border-2 border-dashed border-[#d8bd7a] bg-[#fffaf2] p-8 text-center rounded-lg mb-6">
  <Upload className="w-10 h-10 mx-auto mb-3 text-[#c98905]" />

  <input
    type="file"
    accept=".mp3,.wav,.m4a"
    className="hidden"
    id="demoFileInput"
    onChange={(e) => {
      const file = e.target.files[0];
      setSelectedFile(file);
    }}
  />

  <label
    htmlFor="demoFileInput"
    className="cursor-pointer text-sm text-[#b77900] hover:underline"
  >
    Click to choose file
  </label>

  {selectedFile && (
    <p className="text-xs text-green-600 mt-2">
      Selected: {selectedFile.name}
    </p>
  )}

  <p className="text-xs text-slate-500 mt-2">
    MP3, WAV, M4A • Max 50MB
  </p>
</div>

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => setIsUploadOpen(false)}
        >
          Cancel
        </Button>

        <Button
          onClick={async () => {
  if (!selectedFile) {
    toast.error("Please select a file first");
    return;
  }

  const formData = new FormData();
  formData.append("file", selectedFile);

  try {
    await narratorService.uploadDemo(formData);
    toast.success("Demo uploaded successfully");
    setSelectedFile(null);
    setIsUploadOpen(false);
    await loadData();
  } catch (error) {
    console.error(error);
    toast.error("Upload failed");
  }
}}
          className="bg-[#c98905] text-white hover:bg-[#a56f00]"
        >
          Upload Demo
        </Button>
      </div>
    </div>
  </div>
)}




    </OnboardingShell>
  );
}
